import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Block as BlockIcon,
  Campaign as CampaignIcon,
  Chat as ChatIcon,
  CompareArrows as CompareArrowsIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import ResponsiveDialog from '../components/ResponsiveDialog';
import ResponsiveTableContainer from '../components/ResponsiveTableContainer';
import { useIsMobileMd } from '../hooks/useIsMobile';
import type { AdminAnnouncementListRow } from '../types/announcementAdmin';
import type { Category } from '../types/category';
import { resolveMonitoringImageUrl } from '../utils/monitoringPresentation';

const TYPE_LABELS: Record<string, string> = {
  lost: 'Perdu',
  found: 'Trouvé',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  resolved: 'Résolue',
  closed: 'Fermée',
};

const STATUS_COLORS: Record<string, 'success' | 'default' | 'warning'> = {
  active: 'success',
  resolved: 'default',
  closed: 'warning',
};

const SHARE_CHANNEL_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  x: 'X',
  whatsapp: 'WhatsApp',
  copy: 'Copie',
  native: 'Natif',
};

const SHARE_CHANNEL_COLORS: Record<string, 'primary' | 'secondary' | 'info' | 'default' | 'success'> = {
  facebook: 'primary',
  x: 'default',
  whatsapp: 'success',
  copy: 'info',
  native: 'secondary',
};

function ShareSummaryBadges({ summary }: { summary: AdminAnnouncementListRow['share_summary'] }) {
  if (!summary?.total) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="center">
      {summary.channels.map((channel) => (
        <Chip
          key={channel}
          size="small"
          label={`${SHARE_CHANNEL_LABELS[channel] ?? channel}${summary.counts_by_channel[channel] ? ` (${summary.counts_by_channel[channel]})` : ''}`}
          color={SHARE_CHANNEL_COLORS[channel] ?? 'default'}
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function AnnouncementThumbnail({ url }: { url: string | null }) {
  const src = resolveMonitoringImageUrl(url);
  if (!src) {
    return (
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ImageNotSupportedIcon fontSize="small" color="disabled" />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1,
        objectFit: 'cover',
        bgcolor: 'grey.100',
        display: 'block',
      }}
    />
  );
}

const AnnouncementsAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobileMd();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [userIdInput, setUserIdInput] = useState('');
  const [userId, setUserId] = useState<number | undefined>();
  const [type, setType] = useState<'lost' | 'found' | ''>('');
  const [status, setStatus] = useState<'active' | 'resolved' | 'closed' | ''>('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [partnerFilterInput, setPartnerFilterInput] = useState('');
  const [partnerFilter, setPartnerFilter] = useState<{ providerId?: number; onlyIndividual?: boolean }>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteTarget, setDeleteTarget] = useState<AdminAnnouncementListRow | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const invalidateAnnouncements = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
  };

  useEffect(() => {
    const flashMessage = (location.state as { flashMessage?: string } | null)?.flashMessage;
    if (!flashMessage) return;
    setSnackbar({ open: true, message: flashMessage, severity: 'success' });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'closed' }) =>
      apiService.patchAdminAnnouncementStatus(id, status),
    onSuccess: (res) => {
      invalidateAnnouncements();
      setSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Impossible de modifier le statut.', severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteAdminAnnouncement(id),
    onSuccess: (res) => {
      setDeleteTarget(null);
      invalidateAnnouncements();
      setSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Suppression impossible.', severity: 'error' });
    },
  });

  const recalcMatchMutation = useMutation({
    mutationFn: (id: number) => apiService.recalculateAnnouncementMatches(id),
    onSuccess: (res) => {
      invalidateAnnouncements();
      setSnackbar({ open: true, message: res.message, severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Impossible de recalculer les matchings.', severity: 'error' });
    },
  });

  const { data: providersData } = useQuery({
    queryKey: ['admin-announcements-providers'],
    queryFn: () => apiService.getProviders({ limit: 500 }),
  });

  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
  });

  const categoryOptions: Category[] = useMemo(
    () => [...(categoriesResponse?.data?.categories ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [categoriesResponse],
  );

  const partnerOptions = useMemo(() => {
    const providers = [...(providersData?.providers ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, 'fr'),
    );
    return providers;
  }, [providersData]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'admin-announcements',
      search,
      userId,
      type,
      status,
      categoryId,
      dateFrom,
      dateTo,
      partnerFilter,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      apiService.getAdminAnnouncements({
        search: search || undefined,
        user_id: userId,
        type: type || undefined,
        status: status || undefined,
        category_id: categoryId === '' ? undefined : categoryId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        provider_id: partnerFilter.providerId,
        only_individual: partnerFilter.onlyIndividual,
        page: page + 1,
        limit: rowsPerPage,
      }),
  });

  const rows: AdminAnnouncementListRow[] = useMemo(() => data?.data ?? [], [data]);

  const handleApplyFilters = () => {
    setSearch(searchInput.trim());
    const parsedUserId = userIdInput.trim() ? Number(userIdInput.trim()) : undefined;
    setUserId(Number.isFinite(parsedUserId) ? parsedUserId : undefined);
    if (partnerFilterInput === 'individual') {
      setPartnerFilter({ onlyIndividual: true });
    } else if (partnerFilterInput !== '') {
      setPartnerFilter({ providerId: Number(partnerFilterInput) });
    } else {
      setPartnerFilter({});
    }
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSearch('');
    setUserIdInput('');
    setUserId(undefined);
    setType('');
    setStatus('');
    setCategoryId('');
    setDateFrom('');
    setDateTo('');
    setPartnerFilterInput('');
    setPartnerFilter({});
    setPage(0);
  };

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const formatUser = (row: AdminAnnouncementListRow) => {
    if (!row.user) return '—';
    const name = [row.user.first_name, row.user.last_name].filter(Boolean).join(' ');
    return name ? `${name} (${row.user.email})` : row.user.email;
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <CampaignIcon color="primary" />
        <Typography variant="h4" component="h1">
          Annonces
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 820 }}>
        Consultez toutes les annonces publiées, filtrez par période ou utilisateur, accédez aux matchings et aux
        conversations pour modérer si nécessaire.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Recherche"
                  placeholder="ID, titre, e-mail, lieu, partenaire…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID utilisateur"
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value.replace(/\D/g, ''))}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="lost">Perdu</MenuItem>
                    <MenuItem value="found">Trouvé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select label="Statut" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="resolved">Résolue</MenuItem>
                    <MenuItem value="closed">Fermée</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    label="Catégorie"
                    value={categoryId === '' ? '' : String(categoryId)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCategoryId(v === '' ? '' : Number(v));
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">Toutes</MenuItem>
                    {categoryOptions.map((category) => (
                      <MenuItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Partenaire</InputLabel>
                  <Select
                    label="Partenaire"
                    value={partnerFilterInput}
                    onChange={(e) => setPartnerFilterInput(e.target.value)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="individual">Particuliers uniquement</MenuItem>
                    {partnerOptions.map((provider) => (
                      <MenuItem key={provider.id} value={String(provider.id)}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Du"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Au"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="contained" onClick={handleApplyFilters} fullWidth={isMobile}>
                Filtrer
              </Button>
              <Button variant="outlined" onClick={handleResetFilters} fullWidth={isMobile}>
                Réinitialiser
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les annonces.
        </Alert>
      )}

      <Paper>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Stack spacing={1.5} sx={{ p: 1.5 }}>
                {rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    Aucune annonce trouvée.
                  </Typography>
                ) : (
                  rows.map((row) => (
                    <Card key={row.id} variant="outlined">
                      <CardActionArea onClick={() => navigate(`/announcements/${row.id}`)}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Chip
                              size="small"
                              label={row.matches_count}
                              color={row.matches_count > 0 ? 'primary' : 'default'}
                              variant={row.matches_count > 0 ? 'filled' : 'outlined'}
                              sx={{ mt: 0.5 }}
                            />
                            <AnnouncementThumbnail url={row.thumbnail_url} />
                            <Box minWidth={0} flex={1}>
                              <Typography variant="subtitle2" fontWeight={700} noWrap>
                                {row.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                #{row.id}
                                {row.reference_code ? ` · ${row.reference_code}` : ''}
                              </Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                                <Chip size="small" label={TYPE_LABELS[row.type] ?? row.type} variant="outlined" />
                                <Chip
                                  size="small"
                                  label={STATUS_LABELS[row.status] ?? row.status}
                                  color={STATUS_COLORS[row.status] ?? 'default'}
                                />
                                {row.location?.city ? (
                                  <Chip size="small" label={row.location.city} variant="outlined" />
                                ) : null}
                              </Stack>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                {formatUser(row)} · {formatDate(row.created_at)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Conv. {row.conversations_count}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ px: 1, pb: 1 }}>
                        {row.status === 'active' && (
                          <IconButton
                            size="medium"
                            color="primary"
                            disabled={recalcMatchMutation.isPending}
                            onClick={() => recalcMatchMutation.mutate(row.id)}
                            aria-label="Recalculer les matchings"
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        )}
                        {row.status === 'active' ? (
                          <IconButton
                            size="medium"
                            color="warning"
                            disabled={statusMutation.isPending}
                            onClick={() => statusMutation.mutate({ id: row.id, status: 'closed' })}
                            aria-label="Désactiver l'annonce"
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <BlockIcon />
                          </IconButton>
                        ) : row.status === 'closed' ? (
                          <IconButton
                            size="medium"
                            color="success"
                            disabled={statusMutation.isPending}
                            onClick={() => statusMutation.mutate({ id: row.id, status: 'active' })}
                            aria-label="Réactiver l'annonce"
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        ) : null}
                        <IconButton
                          size="medium"
                          color="error"
                          disabled={deleteMutation.isPending}
                          onClick={() => setDeleteTarget(row)}
                          aria-label="Supprimer l'annonce"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Card>
                  ))
                )}
              </Stack>
            ) : (
            <ResponsiveTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">
                      <CompareArrowsIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Matchings
                    </TableCell>
                    <TableCell align="center" sx={{ width: 56 }}>
                      Recalcul
                    </TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Photo</TableCell>
                    <TableCell>Titre</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Lieu</TableCell>
                    <TableCell>Origine</TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Créée le</TableCell>
                    <TableCell align="center">
                      <ChatIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Conv.
                    </TableCell>
                    <TableCell align="center">
                      <ShareIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Partage
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                        Aucune annonce trouvée.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/announcements/${row.id}`)}
                      >
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.matches_count}
                            color={row.matches_count > 0 ? 'primary' : 'default'}
                            variant={row.matches_count > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {row.status === 'active' ? (
                            <Tooltip title="Recalcul matching + envoi mail">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={recalcMatchMutation.isPending}
                                  onClick={() => recalcMatchMutation.mutate(row.id)}
                                  aria-label="Recalculer les matchings"
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell sx={{ width: 56, p: 1 }}>
                          <AnnouncementThumbnail url={row.thumbnail_url} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 240 }}>
                            {row.title}
                          </Typography>
                          {row.reference_code && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.reference_code}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={TYPE_LABELS[row.type] ?? row.type} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={STATUS_LABELS[row.status] ?? row.status}
                            color={STATUS_COLORS[row.status] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography variant="body2" noWrap title={[row.location?.city, row.location?.postal_code].filter(Boolean).join(' ')}>
                            {row.location?.city ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          {row.location_provider ? (
                            <Chip
                              size="small"
                              icon={<StorefrontIcon />}
                              label={row.location_provider.name}
                              color="info"
                              variant="outlined"
                              title={`Partenaire Sherlook Pro · #${row.location_provider.id}`}
                              sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                            />
                          ) : (
                            <Chip size="small" label="Particulier" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" noWrap title={formatUser(row)}>
                            {formatUser(row)}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(row.created_at)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.conversations_count}
                            color={row.conversations_count > 0 ? 'secondary' : 'default'}
                            variant={row.conversations_count > 0 ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 140 }}>
                          <ShareSummaryBadges summary={row.share_summary} />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {row.status === 'active' ? (
                              <Tooltip title="Désactiver">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    disabled={statusMutation.isPending}
                                    onClick={() =>
                                      statusMutation.mutate({ id: row.id, status: 'closed' })
                                    }
                                    aria-label="Désactiver l'annonce"
                                  >
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : row.status === 'closed' ? (
                              <Tooltip title="Réactiver">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={statusMutation.isPending}
                                    onClick={() =>
                                      statusMutation.mutate({ id: row.id, status: 'active' })
                                    }
                                    aria-label="Réactiver l'annonce"
                                  >
                                    <PlayArrowIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            <Tooltip title="Supprimer définitivement">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={deleteMutation.isPending}
                                  onClick={() => setDeleteTarget(row)}
                                  aria-label="Supprimer l'annonce"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ResponsiveTableContainer>
            )}
            <TablePagination
              component="div"
              count={data?.total ?? 0}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Par page"
            />
          </>
        )}
      </Paper>

      <ResponsiveDialog open={!!deleteTarget} onClose={() => !deleteMutation.isPending && setDeleteTarget(null)} maxWidth="sm">
        <DialogTitle>Supprimer l&apos;annonce</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer définitivement l&apos;annonce <strong>#{deleteTarget?.id}</strong> —{' '}
            <strong>{deleteTarget?.title}</strong> ?
            <br />
            <br />
            Cette action supprime aussi les matchings, conversations, messages et images associés
            (y compris objet trouvé partenaire / miroir FO-CONTACT le cas échéant). Elle est
            irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Annuler
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending || !deleteTarget}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnnouncementsAdminPage;
